import React from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SCQAProcessShowcase from '../components/SCQAProcessShowcase';
import ResearchWorkflowDemo from '../components/ResearchWorkflowDemo';
import SampleReportSection from '../components/SampleReportSection';

const ProductPage = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-midnight-navy to-blue-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0">
              <motion.h1 
                className="text-4xl md:text-5xl font-bold mb-6"
                initial="hidden"
                animate="visible"
                variants={fadeIn}
              >
                Transform Your Equity Research Process
              </motion.h1>
              <motion.p 
                className="text-xl mb-8"
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                transition={{ delay: 0.1 }}
              >
                Research Genie uses AI-powered SCQA methodology to help you generate deeper insights and professional research reports in minutes, not days.
              </motion.p>
              <motion.div
                className="flex flex-col sm:flex-row gap-4"
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                transition={{ delay: 0.2 }}
              >
                <a 
                  href="https://equity-research-analyst-611846019618.us-central1.run.app/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-pale-gold text-midnight-navy px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors text-center"
                >
                  Try Research Genie
                </a>
                <button className="border border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:bg-opacity-10 transition-colors">
                  Watch Demo
                </button>
              </motion.div>
            </div>
            <div className="md:w-1/2">
              <motion.div
                className="bg-white rounded-lg shadow-xl overflow-hidden"
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                transition={{ delay: 0.3 }}
              >
                <img 
                  src="/images/dashboard-screenshot.png" 
                  alt="Research Genie Dashboard" 
                  className="w-full"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Key Features Section */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-midnight-navy mb-4">Streamlined Equity Research Workflow</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Research Genie transforms complex equity analysis into a structured, repeatable process that delivers consistent, high-quality insights.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-md"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.1 }}
            >
              <div className="text-pale-gold text-4xl mb-4">🔍</div>
              <h3 className="text-xl font-bold text-midnight-navy mb-2">AI-Powered Research</h3>
              <p className="text-gray-600">
                Leverage advanced AI to analyze financial data, news, and market trends to generate comprehensive company situations.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-md"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.2 }}
            >
              <div className="text-pale-gold text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-bold text-midnight-navy mb-2">SCQA Methodology</h3>
              <p className="text-gray-600">
                Follow the proven Situation-Complication-Question-Answer framework to structure your analysis and develop compelling investment theses.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-md"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.3 }}
            >
              <div className="text-pale-gold text-4xl mb-4">📊</div>
              <h3 className="text-xl font-bold text-midnight-navy mb-2">Professional Reports</h3>
              <p className="text-gray-600">
                Generate beautifully formatted, comprehensive equity research reports with just one click, ready to share with clients or colleagues.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* SCQA Process Section */}
      <SCQAProcessShowcase />
      
      {/* Research Workflow Demo */}
      <ResearchWorkflowDemo />
      
      {/* Sample Report Section */}
      <SampleReportSection />
      
      {/* Benefits Section */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-midnight-navy mb-4">Why Choose Research Genie</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our AI-powered equity research platform delivers significant advantages for analysts, investors, and financial professionals.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-md"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.1 }}
            >
              <div className="text-pale-gold text-4xl mb-4">⏱️</div>
              <h3 className="text-xl font-bold text-midnight-navy mb-2">Save 80% of Research Time</h3>
              <p className="text-gray-600">
                Reduce research time from days to hours with AI-powered data collection, analysis, and report generation.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-md"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.2 }}
            >
              <div className="text-pale-gold text-4xl mb-4">🧠</div>
              <h3 className="text-xl font-bold text-midnight-navy mb-2">Structured Thinking Framework</h3>
              <p className="text-gray-600">
                The SCQA methodology ensures comprehensive, logical analysis that covers all critical aspects of equity research.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-md"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.3 }}
            >
              <div className="text-pale-gold text-4xl mb-4">📈</div>
              <h3 className="text-xl font-bold text-midnight-navy mb-2">Data-Driven Insights</h3>
              <p className="text-gray-600">
                Access real-time financial data, news sentiment, and market trends to inform your investment decisions.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-md"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.4 }}
            >
              <div className="text-pale-gold text-4xl mb-4">🤖</div>
              <h3 className="text-xl font-bold text-midnight-navy mb-2">AI-Powered Analysis</h3>
              <p className="text-gray-600">
                Leverage advanced AI to identify complications, generate hypotheses, and evaluate investment opportunities.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-md"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.5 }}
            >
              <div className="text-pale-gold text-4xl mb-4">📝</div>
              <h3 className="text-xl font-bold text-midnight-navy mb-2">Professional Reports</h3>
              <p className="text-gray-600">
                Generate beautifully formatted, comprehensive research reports that impress clients and colleagues.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-md"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.6 }}
            >
              <div className="text-pale-gold text-4xl mb-4">🔄</div>
              <h3 className="text-xl font-bold text-midnight-navy mb-2">Consistent Methodology</h3>
              <p className="text-gray-600">
                Ensure consistent, high-quality analysis across all your research projects with our structured approach.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Testimonials Section */}
      <div className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-midnight-navy mb-4">What Our Users Say</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from analysts and investors who have transformed their research process with Research Genie.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div 
              className="bg-white rounded-xl p-8 shadow-md"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold mr-4">JD</div>
                <div>
                  <h3 className="font-bold text-midnight-navy">James Donovan</h3>
                  <p className="text-gray-500">Senior Analyst, Capital Research</p>
                </div>
              </div>
              <p className="text-gray-700 italic mb-4">
                "Research Genie has completely transformed my workflow. What used to take me 2-3 days now takes just a few hours, and the quality of analysis is consistently high. The SCQA methodology ensures I don't miss any critical factors in my research."
              </p>
              <div className="flex text-pale-gold">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
            </motion.div>
            
            <motion.div 
              className="bg-white rounded-xl p-8 shadow-md"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-bold mr-4">ML</div>
                <div>
                  <h3 className="font-bold text-midnight-navy">Maria Lopez</h3>
                  <p className="text-gray-500">Portfolio Manager, Horizon Investments</p>
                </div>
              </div>
              <p className="text-gray-700 italic mb-4">
                "The AI-powered complication identification has helped me spot potential issues I might have otherwise missed. The reports generated by Research Genie are professional and comprehensive, saving me countless hours of work while improving my analysis quality."
              </p>
              <div className="flex text-pale-gold">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-midnight-navy to-blue-900 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Equity Research?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join thousands of analysts and investors who are already using Research Genie to generate deeper insights in less time.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a 
              href="https://equity-research-analyst-611846019618.us-central1.run.app/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-pale-gold text-midnight-navy px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
            >
              Try Research Genie Now
            </a>
            <a 
              href="/pricing" 
              className="border border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:bg-opacity-10 transition-colors"
            >
              View Pricing Plans
            </a>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default ProductPage;
